import {
    CPM_RATES,
    STREAMS_PER_LISTENER,
    SCENARIO_MULTIPLIERS,
    MAX_MONTHLY_GROWTH,
    MIN_MONTHLY_GROWTH,
    PROJECTION_MONTHS,
    NEW_RELEASE_BOOST,
    VERDICT_THRESHOLDS,
    MAX_REASONABLE_SPLIT,
    HOURLY_FLOOR,
    Scenario,
    ServiceKey,
    SERVICE_RATES,
} from './constants';

// --- Growth Rate Calculation ---

export interface HistoricalDataPoint {
    date: Date;
    monthlyListeners: number;
}

export function calculateMonthlyGrowthRate(
    history: HistoricalDataPoint[]
): number {
    if (history.length < 2) return 0;

    const sorted = [...history].sort(
        (a, b) => a.date.getTime() - b.date.getTime()
    );
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    if (!first.monthlyListeners || first.monthlyListeners <= 0) return 0;

    const totalMs = last.date.getTime() - first.date.getTime();
    const totalMonths = totalMs / (1000 * 60 * 60 * 24 * 30.44);
    if (totalMonths < 0.5) return 0;

    // Long-term growth: first → last
    const longTermGrowth =
        Math.pow(last.monthlyListeners / first.monthlyListeners, 1 / totalMonths) - 1;

    // Recent growth: use last ~3 months of data for a more current trend
    let recentGrowth = longTermGrowth;
    if (sorted.length >= 3) {
        // Find point closest to 3 months ago
        const threeMonthsAgo = last.date.getTime() - (90 * 24 * 60 * 60 * 1000);
        let recentStart = sorted[0];
        for (const p of sorted) {
            if (p.date.getTime() >= threeMonthsAgo) {
                recentStart = p;
                break;
            }
        }
        if (recentStart !== last && recentStart.monthlyListeners > 0) {
            const recentMs = last.date.getTime() - recentStart.date.getTime();
            const recentMonths = recentMs / (1000 * 60 * 60 * 24 * 30.44);
            if (recentMonths >= 0.5) {
                recentGrowth = Math.pow(
                    last.monthlyListeners / recentStart.monthlyListeners,
                    1 / recentMonths
                ) - 1;
            }
        }
    }

    // Blend: 60% recent trend, 40% long-term (recent trend matters more for projections)
    const blended = recentGrowth * 0.6 + longTermGrowth * 0.4;

    return Math.max(MIN_MONTHLY_GROWTH, Math.min(MAX_MONTHLY_GROWTH, blended));
}

// --- Listener Projections ---

export interface ListenerProjection {
    month: number;
    conservative: number;
    moderate: number;
    optimistic: number;
}

export function projectListeners(
    currentListeners: number,
    monthlyGrowthRate: number,
    months: number = 36
): ListenerProjection[] {
    const projections: ListenerProjection[] = [];

    for (let m = 1; m <= months; m++) {
        const point: ListenerProjection = {
            month: m,
            conservative: 0,
            moderate: 0,
            optimistic: 0,
        };

        for (const scenario of ['conservative', 'moderate', 'optimistic'] as const) {
            const adjustedRate =
                monthlyGrowthRate * SCENARIO_MULTIPLIERS[scenario];
            const capped = Math.max(
                MIN_MONTHLY_GROWTH,
                Math.min(MAX_MONTHLY_GROWTH, adjustedRate)
            );
            point[scenario] = Math.round(
                currentListeners * Math.pow(1 + capped, m)
            );
        }

        projections.push(point);
    }

    return projections;
}

// --- Per-Track Stream Share ---

/**
 * What fraction of total streams go to your produced track(s).
 * releaseBoost: how many times more streams a new release gets vs catalog average.
 * 2x = normal release, 3-5x = pushed single, 5-10x = viral/playlist push.
 */
export function trackStreamShare(
    catalogSize: number,
    tracksProduced: number,
    releaseBoost: number = NEW_RELEASE_BOOST
): number {
    const effectiveCatalog = catalogSize + tracksProduced;
    const rawShare = (tracksProduced * releaseBoost) / effectiveCatalog;
    return Math.min(rawShare, 1); // cap at 100%
}

// --- Streaming Revenue ---

export interface RevenueProjection {
    period: number; // months
    conservative: number;
    moderate: number;
    optimistic: number;
}

export function calculateStreamingRevenue(
    projections: ListenerProjection[],
    producerSplitPercent: number,
    catalogSize: number,
    tracksProduced: number,
    releaseBoost?: number
): RevenueProjection[] {
    const splitFraction = producerSplitPercent / 100;
    const share = trackStreamShare(catalogSize, tracksProduced, releaseBoost);

    return PROJECTION_MONTHS.map((period) => {
        const result: RevenueProjection = {
            period,
            conservative: 0,
            moderate: 0,
            optimistic: 0,
        };

        for (const scenario of ['conservative', 'moderate', 'optimistic'] as const) {
            let cumulative = 0;
            for (let m = 0; m < period && m < projections.length; m++) {
                const listeners = projections[m][scenario];
                const totalStreams = listeners * STREAMS_PER_LISTENER[scenario];
                const trackStreams = totalStreams * share;
                const monthlyRev = (trackStreams / 1000) * CPM_RATES[scenario] * splitFraction;
                cumulative += monthlyRev;
            }
            result[scenario] = Math.round(cumulative);
        }

        return result;
    });
}

/**
 * Two-phase revenue: use recoupSplit until cumulative revenue >= (standardFee - upfront),
 * then switch to postRecoupSplit for remaining months.
 */
export function calculateTwoPhaseRevenue(
    projections: ListenerProjection[],
    recoupSplit: number,
    postRecoupSplit: number,
    upfront: number,
    standardFee: number,
    catalogSize: number,
    tracksProduced: number,
    releaseBoost?: number
): RevenueProjection[] {
    const recoupFraction = recoupSplit / 100;
    const postRecoupFraction = postRecoupSplit / 100;
    const share = trackStreamShare(catalogSize, tracksProduced, releaseBoost);
    const recoupTarget = Math.max(0, standardFee - upfront);

    return PROJECTION_MONTHS.map((period) => {
        const result: RevenueProjection = {
            period,
            conservative: 0,
            moderate: 0,
            optimistic: 0,
        };

        for (const scenario of ['conservative', 'moderate', 'optimistic'] as const) {
            let cumulative = 0;
            let recouped = false;
            for (let m = 0; m < period && m < projections.length; m++) {
                const listeners = projections[m][scenario];
                const totalStreams = listeners * STREAMS_PER_LISTENER[scenario];
                const trackStreams = totalStreams * share;
                const grossMonthly = (trackStreams / 1000) * CPM_RATES[scenario];
                const splitFraction = recouped ? postRecoupFraction : recoupFraction;
                const monthlyRev = grossMonthly * splitFraction;
                cumulative += monthlyRev;
                if (!recouped && cumulative >= recoupTarget) {
                    recouped = true;
                }
            }
            result[scenario] = Math.round(cumulative);
        }

        return result;
    });
}

// --- Deal Analysis ---

export interface SelectedService {
    key: ServiceKey;
    rate: number;
}

export interface DealAnalysis {
    standardTotal: number;  // fee + standard backend (full deal value)
    standardFee: number;    // just the fee (for recoup timeline)
    artistOfferTotal: number;
    fairSplitPercent: number;
    verdict: 'take' | 'counter' | 'walk';
    verdictReason: string;
    recoupMonths: { conservative: number; moderate: number; optimistic: number };
    opportunityCost: { totalHours: number; effectiveHourly: number; belowFloor: boolean };
}

export function analyzeDeal(
    selectedServices: SelectedService[],
    upfrontOffer: number,
    proposedSplit: number,
    projections: ListenerProjection[],
    catalogSize: number,
    tracksProduced: number,
    riskScore: number,
    releaseBoost?: number,
    postRecoupSplit?: number
): DealAnalysis {
    const standardFee = selectedServices.reduce((sum, s) => sum + s.rate, 0);
    const effectivePostRecoup = postRecoupSplit ?? proposedSplit;

    // Standard deal = fee + standard 20% backend over 24 months
    const standardRoyalty = SERVICE_RATES.fullProduction.royaltyPercent;
    const standardBackend24 = calculateStreamingRevenue(
        projections, standardRoyalty, catalogSize, tracksProduced, releaseBoost
    ).find((r) => r.period === 24)?.moderate ?? 0;
    const standardTotal = standardFee + standardBackend24;

    // Artist's offer = upfront + two-phase backend over 24 months
    const revenueProjections = calculateTwoPhaseRevenue(
        projections, proposedSplit, effectivePostRecoup, upfrontOffer, standardFee,
        catalogSize, tracksProduced, releaseBoost
    );
    const moderate24 = revenueProjections.find((r) => r.period === 24)?.moderate ?? 0;
    const artistOfferTotal = upfrontOffer + moderate24;

    // Fair split = what split makes the offer equal the full standard deal
    // Use flat 100% revenue as the denominator (max possible backend)
    const revAt100 = calculateStreamingRevenue(
        projections, 100, catalogSize, tracksProduced, releaseBoost
    ).find((r) => r.period === 24)?.moderate ?? 0;

    let fairSplitPercent = 0;
    if (revAt100 > 0) {
        fairSplitPercent = Math.min(
            100,
            Math.max(0, ((standardTotal - upfrontOffer) / revAt100) * 100)
        );
    }

    // Recoup months — how long to earn back the standard fee from this deal
    // Uses two-phase split: recoup split until recouped, then post-recoup
    const recoupMonths = { conservative: 0, moderate: 0, optimistic: 0 };
    const share = trackStreamShare(catalogSize, tracksProduced, releaseBoost);
    const recoupTarget = Math.max(0, standardFee - upfrontOffer);

    for (const scenario of ['conservative', 'moderate', 'optimistic'] as const) {
        let cumulative = upfrontOffer;
        let recouped = false;

        for (let m = 0; m < projections.length; m++) {
            const listeners = projections[m][scenario];
            const totalStreams = listeners * STREAMS_PER_LISTENER[scenario];
            const trackStreams = totalStreams * share;
            const grossMonthly = (trackStreams / 1000) * CPM_RATES[scenario];
            const splitFraction = recouped
                ? effectivePostRecoup / 100
                : proposedSplit / 100;
            const monthlyRev = grossMonthly * splitFraction;
            cumulative += monthlyRev;

            if (!recouped && cumulative - upfrontOffer >= recoupTarget) {
                recouped = true;
            }

            if (cumulative >= standardFee) {
                recoupMonths[scenario] = m + 1;
                break;
            }
        }

        if (recoupMonths[scenario] === 0 && cumulative < standardFee) {
            recoupMonths[scenario] = -1; // never within 36 months
        }
    }

    // Risk-adjusted verdict — compare against FULL standard deal value
    const moderateRatio = standardTotal > 0 ? artistOfferTotal / standardTotal : 0;
    const pctStr = Math.round(moderateRatio * 100);

    let verdict: 'take' | 'counter' | 'walk';
    let verdictReason: string;

    const conservativeNeverRecoups = recoupMonths.conservative === -1;
    const highRisk = riskScore >= 7;
    // How much of the deal relies on backend vs upfront
    const backendReliance = moderate24 > 0 ? (moderate24 - standardBackend24) / moderate24 : 0;

    if (moderateRatio >= 1.0) {
        verdict = 'take';
        verdictReason = `The offer covers ${pctStr}% of your full standard deal ($${standardTotal.toLocaleString()}). The math works.`;
        if (highRisk && backendReliance > 0.5) {
            verdictReason += ` Note: high-risk artist — more of this deal is backend-loaded than your standard terms. Consider asking for more upfront.`;
        }
    } else if (moderateRatio >= VERDICT_THRESHOLDS.takeIt) {
        if (!conservativeNeverRecoups && !highRisk) {
            verdict = 'take';
            verdictReason = `The offer covers ${pctStr}% of your full standard deal. Close enough, and risk is manageable.`;
        } else {
            verdict = 'counter';
            const suggestedUpfront90 = Math.max(0, Math.round(standardTotal - moderate24));
            if (fairSplitPercent > MAX_REASONABLE_SPLIT) {
                verdictReason = `The offer covers ${pctStr}% but risk is elevated. Ask for $${suggestedUpfront90.toLocaleString()} upfront at ${proposedSplit}% to close the gap.`;
            } else {
                verdictReason = `The offer covers ${pctStr}% but risk is elevated. Counter with ${fairSplitPercent.toFixed(1)}% backend to close the gap.`;
            }
        }
    } else if (moderateRatio >= VERDICT_THRESHOLDS.counter) {
        verdict = 'counter';
        const suggestedUpfront = Math.max(0, Math.round(standardTotal - moderate24));
        if (proposedSplit >= fairSplitPercent) {
            verdictReason = `The offer covers ${pctStr}% of your full standard deal. The split is fair but upfront is light — ask for $${suggestedUpfront.toLocaleString()} upfront to make it work.`;
        } else if (fairSplitPercent > MAX_REASONABLE_SPLIT) {
            verdictReason = `The offer covers ${pctStr}% of your full standard deal. ${fairSplitPercent.toFixed(0)}% backend isn't realistic — ask for $${suggestedUpfront.toLocaleString()} upfront at ${proposedSplit}%, or $${Math.max(0, Math.round(standardTotal - calcBackendAt(projections, MAX_REASONABLE_SPLIT, catalogSize, tracksProduced, releaseBoost))).toLocaleString()} upfront at ${MAX_REASONABLE_SPLIT}%.`;
        } else {
            verdictReason = `The offer covers ${pctStr}% of your full standard deal. Counter with ${fairSplitPercent.toFixed(1)}% backend, or ask for $${suggestedUpfront.toLocaleString()} upfront at ${proposedSplit}%.`;
        }
    } else {
        verdict = 'walk';
        const walkUpfront = Math.max(0, Math.round(standardTotal - moderate24));
        verdictReason = `The offer covers just ${pctStr}% of your full standard deal ($${standardTotal.toLocaleString()}). The numbers don't work — they'd need to pay $${walkUpfront.toLocaleString()} upfront at ${proposedSplit}% to make it fair, or just pay the full rate.`;
    }

    // Opportunity cost: hours invested × $50/hr floor
    const totalHours = selectedServices.reduce(
        (sum, s) => sum + (SERVICE_RATES[s.key].hours || 0), 0
    );
    const survivalCost = totalHours * HOURLY_FLOOR;
    const effectiveHourly = totalHours > 0
        ? Math.round(artistOfferTotal / totalHours)
        : 0;
    const belowFloor = effectiveHourly < HOURLY_FLOOR;

    // Add opportunity cost warning to verdict
    if (belowFloor && verdict !== 'walk') {
        verdictReason += ` Warning: at ${totalHours} hours, this deal pays ~$${effectiveHourly}/hr — below your $${HOURLY_FLOOR}/hr survival floor ($${survivalCost.toLocaleString()} minimum for this project).`;
    }

    return {
        standardTotal,
        standardFee,
        artistOfferTotal,
        fairSplitPercent,
        verdict,
        verdictReason,
        recoupMonths,
        opportunityCost: { totalHours, effectiveHourly, belowFloor },
    };
}

// Helper: get 24-month moderate backend at a given split
function calcBackendAt(
    projections: ListenerProjection[],
    splitPercent: number,
    catalogSize: number,
    tracksProduced: number,
    releaseBoost?: number
): number {
    return calculateStreamingRevenue(
        projections, splitPercent, catalogSize, tracksProduced, releaseBoost
    ).find((r) => r.period === 24)?.moderate ?? 0;
}

// --- Deal Comparison Table ---

export interface DealRow {
    label: string;
    upfront: number;
    split: number;
    projected24: number;
    totalValue: number;
}

export function buildDealComparison(
    selectedServices: SelectedService[],
    upfrontOffer: number,
    proposedSplit: number,
    fairSplit: number,
    projections: ListenerProjection[],
    catalogSize: number,
    tracksProduced: number,
    releaseBoost?: number,
    postRecoupSplit?: number
): DealRow[] {
    const standardFee = selectedServices.reduce((sum, s) => sum + s.rate, 0);
    const effectivePostRecoup = postRecoupSplit ?? proposedSplit;

    const calcFlatBackend = (split: number) => {
        const rev = calculateStreamingRevenue(projections, split, catalogSize, tracksProduced, releaseBoost);
        return rev.find((r) => r.period === 24)?.moderate ?? 0;
    };

    const calcTwoPhaseBackend = (recoupSplit: number, postSplit: number, upfront: number) => {
        const rev = calculateTwoPhaseRevenue(
            projections, recoupSplit, postSplit, upfront, standardFee,
            catalogSize, tracksProduced, releaseBoost
        );
        return rev.find((r) => r.period === 24)?.moderate ?? 0;
    };

    const standardSplit = SERVICE_RATES.fullProduction.royaltyPercent;
    const standardBackend = calcFlatBackend(standardSplit);

    const artistBackend = calcTwoPhaseBackend(proposedSplit, effectivePostRecoup, upfrontOffer);
    const fairBackend = calcTwoPhaseBackend(fairSplit, effectivePostRecoup, upfrontOffer);

    return [
        {
            label: 'Standard Deal',
            upfront: standardFee,
            split: standardSplit,
            projected24: standardBackend,
            totalValue: standardFee + standardBackend,
        },
        {
            label: "Artist's Offer",
            upfront: upfrontOffer,
            split: proposedSplit,
            projected24: artistBackend,
            totalValue: upfrontOffer + artistBackend,
        },
        {
            label: 'Calculated Fair Deal',
            upfront: upfrontOffer,
            split: Math.round(fairSplit * 10) / 10,
            projected24: fairBackend,
            totalValue: upfrontOffer + fairBackend,
        },
    ];
}
